import React, { useEffect, useState } from "react";
import { Button, Card, CardBody, Col, Row, Form } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { QualificationsService } from "./service";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import { useTranslation } from "react-i18next";
import FormErrorSummary from "../../shared/components/FormErrorSummary";
import { Switch } from "../../shared/components/Switch";

const schema = yup.object().shape({
  name: yup.string().required('formErrors.nameRequired'),
  allowFreeText: yup.boolean().default(false), // Add allowFreeText to the schema
});

interface QualificationState {
  name: string;
  allowFreeText: boolean; // Add allowFreeText to the interface
}

const QualificationsForm: React.FC = () => {
  const { t } = useTranslation(); // Initialize translation hook
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const [qualificationData, setQualificationData] = useState<QualificationState | null>(null);
  const navigate = useNavigate();
  const { control, handleSubmit, reset, formState: { errors } } = useForm<QualificationState>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: "",
      allowFreeText: false, // Default value for allowFreeText
    }
  });

  useEffect(() => {
    if (isEdit) {
      const fetchQualification = async () => {
        try {
          const response = await QualificationsService.getSingle(+id!);
          const qualification = response;
          setQualificationData({
            name: qualification.name,
            allowFreeText: qualification.allowFreeText || false, // Populate allowFreeText if available
          });
        } catch (error) {
          ErrorToast(error);
        }
      };

      fetchQualification();
    }
  }, [isEdit, id]);

  useEffect(() => {
    if (qualificationData) {
      reset(qualificationData);
    }
  }, [qualificationData, reset]);

  const onSubmit = async (data: QualificationState) => {
    try {
      isEdit
        ? await QualificationsService.update(data, +id!)
        : await QualificationsService.create(data);

      SuccessToast(t(`qualification.${isEdit ? 'successUpdate' : 'successCreate'}`));
      navigate('/qualifications');
      reset();
    } catch (error) {
      ErrorToast(error);
    }
  };

  return (
    <React.Fragment>
      <Row>
        <Col sm={12}>
          <Card>
            <CardBody>
              <Form onSubmit={handleSubmit(onSubmit)}>
                <FormErrorSummary errors={errors} />
                <Row className="g-3">
                  <Col sm={12} xl={6}>
                    <div className="mb-0">
                      <label className="form-label">{t("qualification.name")}</label>
                      <Controller
                        name="name"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="text"
                            className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                            placeholder={t("qualification.enterName")}
                            {...field}
                          />
                        )}
                      />
                      {errors.name && <div className="invalid-feedback">
                        {t(errors.name.message as string)}
                      </div>}
                    </div>
                  </Col>
                  <Col sm={6} xl={2} className="mt-5 mx-2">
                    <div>
                      <Controller
                        name="allowFreeText"
                        control={control}
                        render={({ field }) => (
                          <Switch
                            id="allowFreeText"
                            label={t("qualification.allowFreeText")}
                            className={`${errors.allowFreeText ? "is-invalid" : ""}`}
                            checked={field.value}
                            onChange={(checked) => field.onChange(checked)}
                          />
                        )}
                      />
                      {errors.allowFreeText && (
                        <div className="invalid-feedback">
                          {t(errors.allowFreeText.message as string)}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col sm={12}>
                    <Button type="submit" variant="primary">
                      {t(`qualification.${isEdit ? 'update' : 'create'}`)}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default QualificationsForm;